/**
 * M8 approval (#1809) in the admin UI. The rule itself ("awaiting / approved /
 * declined") is decided on the server (`approvalStateOf` in `@kolula/shared`);
 * the bookings list carries it as `approvalState` and the room-booking detail
 * as `approval.state`. This file only maps it to labels and the list filter,
 * so the list, the detail page and the dashboard link cannot read it
 * differently.
 */
export type ApprovalState = 'awaiting' | 'approved' | 'declined';

const APPROVAL_STATES: readonly ApprovalState[] = ['awaiting', 'approved', 'declined'];

/** Narrow an API value to a known state; anything else is "no sub-status". */
export const toApprovalState = (value: unknown): ApprovalState | null =>
    typeof value === 'string' && (APPROVAL_STATES as readonly string[]).includes(value.toLowerCase())
        ? (value.toLowerCase() as ApprovalState)
        : null;

/** i18n key of the sub-status shown next to the status badge. */
export const approvalLabelKey = (state: ApprovalState): string =>
    `booking_details.approval.${state}`;

/**
 * `?approval=awaiting` on the bookings list: the dashboard's "Awaiting
 * approval" figure links there (`makeBookingsAwaitingApprovalUrl`).
 */
export const APPROVAL_QUERY_PARAM = 'approval';
export const AWAITING_APPROVAL_QUERY_VALUE = 'awaiting';

export const isAwaitingApproval = (value: unknown): boolean =>
    toApprovalState(value) === 'awaiting';

/**
 * The edit pages' "save and send email" copy. On a reservation awaiting
 * approval that send IS the approval, so the button and its confirm say so.
 */
export const saveAndSendCopyKeys = (awaitingApproval: boolean) => (awaitingApproval
    ? {
        button: 'edit_booking.button.approve_and_send',
        title: 'edit_booking.approve_modal.title',
        description: 'edit_booking.approve_modal.description',
    }
    : {
        button: 'edit_booking.button.confirm_and_send',
        title: 'edit_booking.modal.title',
        description: 'edit_booking.modal.description',
    });
